/**
 * VORE — comportamento das paginas institucionais (vore-pages.js)
 *
 * Um custom element, com degradacao graciosa (sem JS o HTML continua
 * utilizavel e completo):
 *
 *   <vore-tabs>      abas do FAQ (/pages/faq). Sincroniza botoes e paineis e
 *                   posiciona a barra deslizante (.vore-faq-tabs__indicator)
 *                   com getBoundingClientRect, como o JS inline do site.
 *                   Sem JS: o <noscript> da secao mostra os 4 paineis de uma
 *                   vez e esconde a barra de abas — nenhuma pergunta some.
 *                   Acrescenta ao original: roles ARIA, navegacao por setas
 *                   e reposicionamento da barra em resize.
 *
 * Os carrosseis destas paginas (13 ingredientes e "explore the range") usam o
 * componente compartilhado <vore-carousel> de assets/vore-carousel.js.
 *
 * Ver VORE-DIVERGENCIAS.md, etapa `pages-inst`.
 */

class RstTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-vore-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-vore-panel]'));
    this.indicator = this.querySelector('[data-vore-indicator]');
    this.tablist = this.querySelector('[data-vore-tablist]');

    if (this.tabs.length === 0 || this.panels.length === 0) return;

    this.onTabClick = this.onTabClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.moveIndicator = this.moveIndicator.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onKeydown);
    });

    const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.select(initial, false);

    window.addEventListener('resize', this.moveIndicator);

    if ('ResizeObserver' in window && this.tablist) {
      this.observer = new ResizeObserver(this.moveIndicator);
      this.observer.observe(this.tablist);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.moveIndicator).catch(() => {});
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.moveIndicator);
    if (this.observer) this.observer.disconnect();
  }

  onTabClick(event) {
    this.select(event.currentTarget, true);
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.currentTarget);
    if (index === -1) return;

    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    const target = this.tabs[(next + this.tabs.length) % this.tabs.length];
    this.select(target, true);
    target.focus();
  }

  select(tab, moveFocusablePanel) {
    if (!tab) return;
    const id = tab.getAttribute('data-vore-tab');

    this.tabs.forEach((item) => {
      const active = item === tab;
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-vore-panel') !== id;
    });

    this.moveIndicator();

    if (moveFocusablePanel) {
      this.dispatchEvent(new CustomEvent('rst:tabchange', { bubbles: true, detail: { tab: id } }));
    }
  }

  moveIndicator() {
    if (!this.indicator || !this.tablist) return;
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!active) return;

    const containerRect = this.tablist.getBoundingClientRect();
    const tabRect = active.getBoundingClientRect();

    this.indicator.style.left = `${tabRect.left - containerRect.left}px`;
    this.indicator.style.width = `${tabRect.width}px`;
  }
}

if (!customElements.get('vore-tabs')) customElements.define('vore-tabs', RstTabs);
